import { formatProductCard, type RGProduct } from "@raumagarn/formatters";
import { defineFormatter } from "@trydig/centra/formatting";
import {
	generateVariants,
	getBooklet,
	getIssuuURL,
	getOneSize,
	getProductBreadcrumbs,
	getRelatedProducts,
	getSizeChart,
	getSizes,
	getYarnRelation,
} from "./utils";

export const formatProductDetails = defineFormatter(
	(product: RGProduct) => {
		const type = product.pr_product_type_type;
		const rootProduct = formatProductCard(product);
		const breadCrumbs = getProductBreadcrumbs(product);
		const variants = generateVariants(rootProduct);
		const sizes = getSizes(product);
		const relatedProducts = getRelatedProducts(product);
		const size = getOneSize(product);

		return {
			...(rootProduct as Omit<typeof rootProduct, "relatedProducts" | "variants">),
			description: product.description,
			sku: product.sku,
			combinations: product.pr_combinations_value,
			care: product.pr_care?.map((item) => ({
				text: item.label,
				illustrationUrl: item.icon?.url,
			})),
			items: product.items,
			brandName: "Rauma Garn",
			categoryUri: product.categoryUri,
			production: product.pr_production_value,
			fits: product.pr_fits?.join(", "),
			categories: product.categories,
			cannonicalCategory: product?.categoryName?.[0],
			categoryName: product.categoryName,
			knittingGauge: product.pr_knitting_gauge,
			knittingGaugeText: product.pr_knitting_direction_value,
			needleSize: product.pr_needle_size,
			issuuURL: getIssuuURL(product),
			composition: product.pr_composition_text_compositionText,
			needles: product.pr_needle_size || product.var_needle_size,
			wireNeedle: product.pr_needle_wire === "1",
			breadcrumbs: getProductBreadcrumbs(product),
			yarns: getYarnRelation(product),
			cloudinaryId: product.pr_cloudinary_id_cloudinaryId,
			/**
			 * Recipe products only
			 */
			washGuide: product.pr_wash_guide_value,
			booklet: type === "recipe" ? getBooklet(rootProduct) : undefined,

			descriptionHTML: product.descriptionHtml,
			sizeChart: getSizeChart(product),
			centraProduct: product.centraProduct,
			free: product.pr_free_recipe_freeRecipe === "1",
			weight: product.pr_display_weight_value,
			newVariantColor: product.var_new_color_extended === "1",
			collectionName: product.collectionName,
			recipePDF: type === "recipe" ? product?.pr_cloudinary_id_cloudinaryId : undefined,
			recipeDifficulty: type === "recipe" ? product.pr_recipe_difficulty_value : undefined,
			recipeLanguageRelations:
				type === "recipe"
					? rootProduct.relatedProducts
							?.filter((dis) => dis.relation === "Language recipe")
							.filter((dis) => dis.productType === "recipe")
							.filter((dis) => !!dis.language && !!dis.uri)
							.map((dis) => ({
								uri: dis.uri,
								language: dis.language,
							}))
					: undefined,
			freeRecipe: type === "recipe" ? rootProduct.freeRecipe : undefined,
			type,
			product: product.product,
			prices: product.prices,
			priceAsNumber: product.priceAsNumber,
			issuuUrl: product.pr_issuu_url_length,
			breadCrumbs,
			variants,
			sizes,
			relatedProducts,
			size,
			availableRecipeTypes: product.pr_recipe_available_types,
		};
	},
	{ displayAsVariant: true },
);

export { getNeedleItemsFromSizes } from "./utils";

export type ProductDetails = ReturnType<typeof formatProductDetails>;
